import { makeAutoObservable, runInAction } from 'mobx';
import { sortBy } from 'lodash-es';
import { v4 } from 'uuid';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { AggFuncDto, CmApi, IndicatorDto, VarSourceDto } from '@src/api';

import { indicatorsStore } from '../indicatorsStore';

class IndicatorEditStore {
  indicator: IndicatorDto | null;

  varSources: VarSourceDto[] = [];

  aggFuncs: AggFuncDto[] = [];

  isNew = false;

  editMode = false;

  sourceFields: { id: string; name: string }[] = [];

  groupingColumns: { id: string; name: string; table: string }[] = [];

  api: CmApi;

  hiddenEdit = false;

  hiddenDelete = false;

  hiddenClean = false;

  hiddenSave = false;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    this.indicator = null;

    makeAutoObservable(this);
  }

  get isEditMode() {
    return this.editMode || this.isNew;
  }

  setNew = (isNew = true) => {
    this.isNew = isNew;
    this.indicator = null;
  };

  setEditMode = (editMode = true) => {
    this.editMode = editMode;
  };

  save = async (indicator: IndicatorDto) => {
    preloaderStore.preloader(true);

    const { data } = await (this.isNew
      ? this.api.indicators.addIndicators([indicator])
      : this.api.indicators.updateIndicators([indicator]));

    if (data) {
      indicatorsStore.setIsChanged(true);

      if (this.isNew) {
        preloaderStore.preloader(false);
        return { varCode: data[0].varCode };
      }
      runInAction(() => {
        this.indicator = {
          ...data[0],
          filters:
            data[0].filters?.map((item) => ({ ...item, fieldName: `${data[0].techSourceTable}.${item.fieldName}` })) ??
            [],
        };
        this.setEditMode(false);
      });
    }

    preloaderStore.preloader(false);
    return undefined;
  };

  delete = async () => {
    if (this.indicator) {
      await this.api.indicators.deleteIndicatorsByCode(this.indicator.varCode ?? '');
      indicatorsStore.setIsChanged(true);
    }
  };

  fetch = async (varCode?: string): Promise<void> => {
    if (!varCode) {
      return;
    }
    preloaderStore.preloader(true);
    runInAction(() => {
      this.setEditMode(false);
      this.setNew(false);
    });

    const { data } = await this.api.indicators.getIndicatorByVarCode(varCode);

    if (data) {
      runInAction(() => {
        this.indicator = {
          ...data,
          filters:
            data.filters?.map((item) => ({
              ...item,
              fieldName: `${data.techSourceTable}.${item.fieldName}`,
              id: v4(),
            })) ?? [],
        };
      });
    }
    preloaderStore.preloader(false);
  };

  getTableFieldsInfo = async (reportVersionCode: string): Promise<void> => {
    preloaderStore.preloader(true);
    try {
      const { data } = await this.api.tableFieldsInfo.getTableFieldsInfo(reportVersionCode);

      if (data) {
        runInAction(() => {
          this.sourceFields = sortBy(data, ['tableName', 'orderNo', 'columnName', 'columnComment'])
            .filter((item) => item.columnName)
            .map((item) => ({
              id: `${item.tableName}.${item.columnName}`,
              name: `${item.columnComment} [${item.tableName}] ${item.columnName}`,
            }));
        });

        const out = {};

        data.forEach((item) => {
          if (item.groupNumber) {
            const groupOrder = item.groupOrder?.split(' ')?.filter((el) => el !== '_');

            item.groupNumber
              .split(' ')
              .filter((el) => el)
              .forEach((group, index) => {
                const key = `${item.tableName} ${group}`;
                out[key] = [...(out[key] ?? []), { comment: item.columnComment, order: +(groupOrder?.[index] ?? 0) }];
              });
          }
        });

        runInAction(() => {
          const sortKey = sortBy(Object.keys(out));

          this.groupingColumns = sortKey.map((key) => {
            const sortedName = sortBy(out[key], 'order');
            const joinName = sortedName.map((name) => name.comment).join(', ');
            const table = key.split(' ').at(0);
            const group = key.split(' ').at(-1);

            return {
              name: `[${table}] ${group} - ${joinName}`,
              id: `${group}`,
              table: `${table}`,
            };
          });
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      preloaderStore.preloader(false);
    }
  };

  getVarSources = async (): Promise<void> => {
    preloaderStore.preloader(true);
    try {
      const { data } = await this.api.indicators.getVarSources();

      if (data) {
        runInAction(() => {
          this.varSources = data?.map((item) => ({ ...item, id: item.varSource })) ?? [];
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      preloaderStore.preloader(false);
    }
  };

  getAggFuncs = async (): Promise<void> => {
    preloaderStore.preloader(true);
    try {
      const { data } = await this.api.indicators.getAggFuncs();

      if (data) {
        runInAction(() => {
          this.aggFuncs = data?.map((item) => ({ ...item, id: item.aggFunction })) ?? [];
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      preloaderStore.preloader(false);
    }
  };

  setHiddenEdit = (hiddenEdit = true): void => {
    this.hiddenEdit = hiddenEdit;
  };

  setHiddenDelete = (hiddenDelete = true): void => {
    this.hiddenDelete = hiddenDelete;
  };

  setHiddenClean = (hiddenClean = true): void => {
    this.hiddenClean = hiddenClean;
  };

  setHiddenSave = (hiddenSave = true): void => {
    this.hiddenSave = hiddenSave;
  };

  clear = () => {
    this.sourceFields = [];
    this.groupingColumns = [];
  };
}

export const indicatorEditStore = new IndicatorEditStore({ api: cmApi });
